import { looksLikeHtml, sanitizeRichTextHtml } from "./richText";

const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const HTML_TAGS = /<\/?[a-z][^>]*>/gi;
const DANGEROUS_PROTOCOL = /^(?:javascript|vbscript|data|file):/i;
const SENSITIVE_KEY = /(?:password|passwd|secret|token)$/i;

export type SanitizeMode = "plain" | "html-aware";

export function sanitizeUserInput(value: string): string {
  return value
    .replace(CONTROL_CHARS, "")
    .replace(HTML_TAGS, "")
    .replace(/^\s*(?:javascript|vbscript|data\s*:\s*text\/html)\s*:/i, "");
}

export function sanitizeSearchQuery(value: string): string {
  return sanitizeUserInput(value);
}

export function sanitizeHref(value: string | null | undefined): string {
  if (!value) return "";
  const trimmed = value.replace(CONTROL_CHARS, "").trim();
  if (!trimmed || DANGEROUS_PROTOCOL.test(trimmed)) return "";
  return sanitizeUserInput(trimmed);
}

export function sanitizeString(value: string, mode: SanitizeMode = "plain"): string {
  const cleaned = value.replace(CONTROL_CHARS, "");
  if (mode === "html-aware" && looksLikeHtml(cleaned)) {
    return sanitizeRichTextHtml(cleaned);
  }
  return sanitizeUserInput(cleaned);
}

export function sanitizeInputTree<T>(
  value: T,
  mode: SanitizeMode = "plain",
): T {
  if (value == null || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return sanitizeString(value, mode) as T;
  }
  if (typeof value !== "object" || value instanceof Date || value instanceof Blob) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeInputTree(item, mode)) as T;
  }

  const record = value as Record<string, unknown>;
  const next: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(record)) {
    if (typeof nested === "string" && SENSITIVE_KEY.test(key)) {
      next[key] = nested.replace(CONTROL_CHARS, "");
      continue;
    }
    next[key] = sanitizeInputTree(nested, mode);
  }
  return next as T;
}
